import { Reader } from '../bytes/reader';
import { ParseError } from '../bytes/errors';
import { concat, fromBase64 } from '../bytes/encoding';
import { readDigitallySigned, u16, u8, u64, vec, writeDigitallySigned } from '../tls/vectors';
import { verifyDigitallySigned, type PublicKey, type VerifyResult } from '../crypto/sig';
import { encodeSignedEntry, entryTypeCode } from './leaf';
import { CT_V1, SignatureType, type Sct, type SignedEntry } from './types';

export function readSct(r: Reader): Sct {
  const start = r.pos;
  const version = r.u8();
  if (version !== CT_V1) throw new ParseError(`unsupported SCT version ${version}`, start);
  const logId = r.bytes(32);
  const timestamp = r.u64n();
  const extensions = r.vec(2);
  const signature = readDigitallySigned(r);
  return { version, logId, timestamp, extensions, signature, raw: r.buf.subarray(start, r.pos) };
}

export function parseSct(raw: Uint8Array): Sct {
  const r = new Reader(raw);
  const sct = readSct(r);
  r.expectEnd('SCT');
  return sct;
}

export function encodeSct(s: Omit<Sct, 'raw'>): Uint8Array {
  return concat(u8(s.version), s.logId, u64(s.timestamp), vec(2, s.extensions), writeDigitallySigned(s.signature));
}

/** RFC 6962 §3.3 SignedCertificateTimestampList: u16 total, then repeated u16-prefixed SCTs. */
export function parseSctList(raw: Uint8Array): Sct[] {
  const r = new Reader(raw);
  const list = r.subVec(2);
  r.expectEnd('SignedCertificateTimestampList');
  const out: Sct[] = [];
  while (!list.eof) out.push(parseSct(list.vec(2)));
  return out;
}

export function encodeSctList(scts: Uint8Array[]): Uint8Array {
  return vec(2, concat(...scts.map((s) => vec(2, s))));
}

export interface SctJson {
  sct_version: number;
  id: string;
  timestamp: number;
  extensions: string;
  signature: string;
}

/** Builds an SCT from an add-chain JSON response. */
export function sctFromJson(json: unknown): Sct {
  const j = json as Partial<SctJson>;
  if (typeof j?.sct_version !== 'number' || typeof j.id !== 'string' || typeof j.timestamp !== 'number' || typeof j.signature !== 'string') {
    throw new ParseError('add-chain response: missing or malformed fields');
  }
  const logId = fromBase64(j.id);
  if (logId.length !== 32) throw new ParseError('add-chain response: id is not 32 bytes');
  const extensions = fromBase64(j.extensions ?? '');
  const sigBytes = fromBase64(j.signature);
  const signature = readDigitallySigned(new Reader(sigBytes));
  const partial = { version: j.sct_version, logId, timestamp: j.timestamp, extensions, signature };
  return { ...partial, raw: encodeSct(partial) };
}

/** RFC 6962 §3.2 — the bytes an SCT signature covers. */
export function sctSignedInput(sct: Pick<Sct, 'version' | 'timestamp' | 'extensions'>, entry: SignedEntry): Uint8Array {
  return concat(
    u8(sct.version),
    u8(SignatureType.certificate_timestamp),
    u64(sct.timestamp),
    u16(entryTypeCode(entry.type)),
    encodeSignedEntry(entry),
    vec(2, sct.extensions),
  );
}

export function verifySct(sct: Sct, entry: SignedEntry, key: PublicKey): Promise<VerifyResult> {
  return verifyDigitallySigned(key, sct.signature, sctSignedInput(sct, entry));
}

/** static-ct-api CtExtensions: a list of { u8 type, u16 length, data }. */
export interface CtExtension {
  type: number;
  data: Uint8Array;
}
export const CT_EXT_LEAF_INDEX = 0;

export function parseCtExtensions(ext: Uint8Array): CtExtension[] {
  const r = new Reader(ext);
  const out: CtExtension[] = [];
  while (!r.eof) out.push({ type: r.u8(), data: r.vec(2) });
  return out;
}

/** The leaf_index extension (type 0, 40-bit big-endian index) that static-ct-api logs put in every SCT. */
export function leafIndexFromExtensions(ext: Uint8Array): number | undefined {
  try {
    for (const e of parseCtExtensions(ext)) {
      if (e.type === CT_EXT_LEAF_INDEX && e.data.length === 5) return new Reader(e.data).u40();
    }
  } catch {
    /* malformed extensions are not fatal; treat as absent */
  }
  return undefined;
}
